"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var d3_color_1 = require("d3-color");
var lodash_1 = require("lodash");
var defaultTypeStyles_1 = require("../customization/defaultTypeStyles");
var defaultLinkStyles_1 = require("../customization/defaultLinkStyles");
var templates_1 = require("../customization/templates");
var blankNodes_1 = require("../data/sparql/blankNodes");
var utils_1 = require("../data/utils");
var events_1 = require("../viewUtils/events");
var model_1 = require("./model");
var linkRouter_1 = require("./linkRouter");
var RenderingLayer;
(function (RenderingLayer) {
    RenderingLayer[RenderingLayer["Element"] = 1] = "Element";
    RenderingLayer[RenderingLayer["ElementSize"] = 2] = "ElementSize";
    RenderingLayer[RenderingLayer["PaperArea"] = 3] = "PaperArea";
    RenderingLayer[RenderingLayer["Link"] = 4] = "Link";
    RenderingLayer[RenderingLayer["FirstToUpdate"] = 1] = "FirstToUpdate";
    RenderingLayer[RenderingLayer["LastToUpdate"] = 4] = "LastToUpdate";
})(RenderingLayer = exports.RenderingLayer || (exports.RenderingLayer = {}));
var DiagramView = /** @class */ (function () {
    function DiagramView(model, options) {
        if (options === void 0) { options = {}; }
        this.model = model;
        this.options = options;
        this.listener = new events_1.EventObserver();
        this.source = new events_1.EventSource();
        this.events = this.source;
        this.disposed = false;
        this.colorSeed = 0x0BADBEEF;
        this._language = 'en';
        this.linkTemplates = new Map();
        this.typeStyleResolvers = options.typeStyleResolvers
            ? options.typeStyleResolvers : defaultTypeStyles_1.DefaultTypeStyleBundle;
        this.linkTemplateResolvers = options.linkTemplateResolvers
            ? this.options.linkTemplateResolvers : defaultLinkStyles_1.DefaultLinkTemplateBundle;
        this.templatesResolvers = options.templatesResolvers
            ? options.templatesResolvers : templates_1.DefaultTemplateBundle;
        this.initRouting();
    }
    DiagramView.prototype.initRouting = function () {
        var _this = this;
        this.router = this.options.linkRouter || new linkRouter_1.DefaultLinkRouter();
        this.updateRoutings();
        this.listener.listen(this.model.events, 'changeCells', function () { return _this.updateRoutings(); });
        this.listener.listen(this.model.events, 'linkEvent', function (_a) {
            var key = _a.key, data = _a.data;
            if (data.changeVertices) {
                _this.updateRoutings();
            }
        });
        this.listener.listen(this.model.events, 'elementEvent', function (_a) {
            var key = _a.key, data = _a.data;
            if (data.changePosition || data.changeSize) {
                _this.updateRoutings();
            }
        });
    };
    DiagramView.prototype.updateRoutings = function () {
        this.routings = this.router.route(this.model);
    };
    DiagramView.prototype.getRouting = function (linkId) {
        return this.routings[linkId];
    };
    DiagramView.prototype.getLanguage = function () { return this._language; };
    DiagramView.prototype.setLanguage = function (value) {
        if (!value) {
            throw new Error('Cannot set empty language.');
        }
        var previous = this._language;
        if (previous === value) {
            return;
        }
        this._language = value;
        this.source.trigger('changeLanguage', { source: this, previous: previous });
    };
    DiagramView.prototype.getLinkTemplates = function () {
        return this.linkTemplates;
    };
    DiagramView.prototype.performSyncUpdate = function () {
        for (var layer = RenderingLayer.FirstToUpdate; layer <= RenderingLayer.LastToUpdate; layer++) {
            this.source.trigger('syncUpdate', { layer: layer });
        }
    };
    DiagramView.prototype.onIriClick = function (iri, element, event) {
        event.persist();
        event.preventDefault();
        var onIriClick = this.options.onIriClick;
        if (onIriClick) {
            onIriClick(iri, element, event);
        }
    };
    DiagramView.prototype.setPaperWidget = function (widget) {
        var key = widget.key, element = widget.widget, pinnedToScreen = widget.pinnedToScreen;
        var widgets = (_a = {}, _a[widget.key] = element ? { element: element, pinnedToScreen: pinnedToScreen } : undefined, _a);
        this.source.trigger('updateWidgets', { widgets: widgets });
        var _a;
    };
    /**
     * Obsolete. Use `chooseLocalizedText()` or `formatLocalizedLabel()` instead.
     * @deprecated
     */
    DiagramView.prototype.getLocalizedText = function (texts) {
        return model_1.chooseLocalizedText(texts, this.getLanguage());
    };
    DiagramView.prototype.getElementTypeString = function (elementModel) {
        var _this = this;
        return elementModel.types.map(function (typeId) {
            var type = _this.model.createClass(typeId);
            return _this.getElementTypeLabel(type).text;
        }).sort().join(', ');
    };
    DiagramView.prototype.getElementTypeLabel = function (type) {
        var label = this.getLocalizedText(type.label);
        return label ? label : { text: utils_1.uri2name(type.id), lang: '' };
    };
    DiagramView.prototype.getLinkLabel = function (linkTypeId) {
        var type = this.model.createLinkType(linkTypeId);
        var label = type ? this.getLocalizedText(type.label) : null;
        return label ? label : { text: utils_1.uri2name(linkTypeId), lang: '' };
    };
    DiagramView.prototype.getTypeStyle = function (types) {
        types.sort();
        var customStyle;
        for (var _i = 0, _a = this.typeStyleResolvers; _i < _a.length; _i++) {
            var resolver = _a[_i];
            var result = resolver(types);
            if (result) {
                customStyle = result;
                break;
            }
        }
        var icon = customStyle ? customStyle.icon : undefined;
        var color;
        if (customStyle && customStyle.color) {
            color = d3_color_1.hcl(customStyle.color);
        }
        else {
            var hue = getHueFromClasses(types, this.colorSeed);
            color = { h: hue, c: 40, l: 75 };
        }
        return { icon: icon, color: color };
    };
    DiagramView.prototype.formatIri = function (iri) {
        if (blankNodes_1.isEncodedBlank(iri)) {
            return '(blank node)';
        }
        return "<" + iri + ">";
    };
    DiagramView.prototype.registerElementStyleResolver = function (resolver) {
        this.typeStyleResolvers.unshift(resolver);
        return resolver;
    };
    DiagramView.prototype.unregisterElementStyleResolver = function (resolver) {
        var index = this.typeStyleResolvers.indexOf(resolver);
        if (index !== -1) {
            return this.typeStyleResolvers.splice(index, 1)[0];
        }
        else {
            return undefined;
        }
    };
    DiagramView.prototype.getElementTemplate = function (types) {
        for (var _i = 0, _a = this.templatesResolvers; _i < _a.length; _i++) {
            var resolver = _a[_i];
            var result = resolver(types);
            if (result) {
                return result;
            }
        }
        return templates_1.StandardTemplate;
    };
    DiagramView.prototype.registerTemplateResolver = function (resolver) {
        this.templatesResolvers.unshift(resolver);
        return resolver;
    };
    DiagramView.prototype.unregisterTemplateResolver = function (resolver) {
        var index = this.templatesResolvers.indexOf(resolver);
        if (index !== -1) {
            return this.templatesResolvers.splice(index, 1)[0];
        }
        else {
            return undefined;
        }
    };
    DiagramView.prototype.createLinkTemplate = function (linkType) {
        var existingTemplate = this.linkTemplates.get(linkType.id);
        if (existingTemplate) {
            return existingTemplate;
        }
        var template = {};
        for (var _i = 0, _a = this.linkTemplateResolvers; _i < _a.length; _i++) {
            var resolver = _a[_i];
            var result = resolver(linkType.id);
            if (result) {
                template = lodash_1.cloneDeep(result);
                break;
            }
        }
        fillLinkTemplateDefaults(template);
        this.linkTemplates.set(linkType.id, template);
        this.source.trigger('changeLinkTemplates', {});
        return template;
    };
    DiagramView.prototype.registerLinkTemplateResolver = function (resolver) {
        this.linkTemplateResolvers.unshift(resolver);
        return resolver;
    };
    DiagramView.prototype.unregisterLinkTemplateResolver = function (resolver) {
        var index = this.linkTemplateResolvers.indexOf(resolver);
        return index >= 0 ? this.linkTemplateResolvers.splice(index, 1)[0] : undefined;
    };
    DiagramView.prototype.dispose = function () {
        if (this.disposed) {
            return;
        }
        this.source.trigger('dispose', {});
        this.listener.stopListening();
        this.disposed = true;
    };
    return DiagramView;
}());
exports.DiagramView = DiagramView;
function getHueFromClasses(classes, seed) {
    var hash = seed;
    for (var _i = 0, classes_1 = classes; _i < classes_1.length; _i++) {
        var name_1 = classes_1[_i];
        hash = utils_1.hashFnv32a(name_1, hash);
    }
    var MAX_INT32 = 0x7fffffff;
    return 360 * ((hash === undefined ? 0 : hash) / MAX_INT32);
}
function fillLinkTemplateDefaults(template) {
    var defaults = {
        markerTarget: { d: 'M0,0 L0,8 L9,4 z', width: 9, height: 8, fill: 'black' },
    };
    lodash_1.defaultsDeep(template, defaults);
    if (!template.renderLink) {
        template.renderLink = function () { return ({}); };
    }
}
